import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/formatters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Eye, EyeSlash, Warning } from "@phosphor-icons/react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/settings/branding`);
        if (!cancelled) setBranding(data);
      } catch (err) {
        console.error("Failed to load branding:", err);
      } finally {
        if (!cancelled) setBrandingLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (user) {
    const isAdmin = user.role === "superadmin" || (user.permissions && user.permissions.length > 0);
    return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
  }

  // Wait for branding to load before rendering
  if (!brandingLoaded) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const b = branding || {};
  const systemName = b.system_name || "";
  const subtitle = b.login_subtitle || "";
  const primaryColor = b.primary_color || "#007AFF";
  const bgUrl = b.login_bg_url || "";
  const logoUrl = b.logo_url || "";
  const headingFont = b.heading_font || "sans-serif";
  const version = b.system_version || "";
  const emailPlaceholder = b.login_email_placeholder || "Username";
  const passwordPlaceholder = b.login_password_placeholder || "";
  const buttonText = b.login_button_text || "LOGIN";
  const footerText = b.login_footer_text || "";

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      {/* Background */}
      {bgUrl ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgUrl})` }}
        />
      ) : null}
      <div className="absolute inset-0 bg-black/80" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-slide-up">
        <div className="bg-[#121212] border border-[#262626] rounded-sm p-8">
          {/* Logo / Title */}
          <div className="text-center mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="mx-auto mb-4 h-14 w-auto object-contain" data-testid="login-logo" />
            ) : primaryColor ? (
              <div
                className="inline-flex items-center justify-center w-14 h-14 border rounded-sm mb-4"
                style={{ background: `${primaryColor}15`, borderColor: `${primaryColor}40` }}
              >
                <ShieldCheck size={28} weight="bold" style={{ color: primaryColor }} />
              </div>
            ) : null}
            {systemName && (
              <h1
                className="text-2xl font-bold text-white tracking-tight"
                style={{ fontFamily: `'${headingFont}', sans-serif` }}
                data-testid="login-title"
              >
                {systemName}
              </h1>
            )}
            {subtitle && <p className="text-sm text-[#A3A3A3] mt-1 uppercase tracking-[0.2em]">{subtitle}</p>}
            {version && <p className="text-[10px] text-[#737373] mt-1 font-mono-data">{version}</p>}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-3 py-2 rounded-sm mb-4 text-sm" data-testid="login-error">
              <Warning size={16} weight="bold" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Username</Label>
              <Input
                data-testid="login-email-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={emailPlaceholder}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-[#737373] rounded-sm"
                style={{ borderColor: "initial" }}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={passwordPlaceholder}
                  required
                  className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-[#737373] rounded-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full text-white font-semibold rounded-sm h-10 transition-all duration-200"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                buttonText
              )}
            </Button>
          </form>

          {footerText && (
            <p className="text-xs text-[#737373] text-center mt-6">
              {footerText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
